import { ColumnState } from 'ag-grid-community';
import { create } from 'zustand';

import {
	defaultTQC2090_R01Dto,
	TQC2090_R01_COMP_CD,
	TQC2090_R01Dto,
	TQC2090_R01_LINE_OPTIONS,
} from './Dto_TQC2090_R01';

export interface TQC2090_R01Filter {
	compCd: string;
	/** 검사연월. Date 로 보관하고 전송 시 yyyy-MM 으로 변환한다. */
	inspectYm: Date | undefined;
	/** 공정라인 (탭) */
	workplaceCd: string;
}

const firstDayOfThisMonth = () => {
	const today = new Date();
	return new Date(today.getFullYear(), today.getMonth(), 1);
};

export const defaultTQC2090_R01Filter: TQC2090_R01Filter = {
	compCd: TQC2090_R01_COMP_CD,
	inspectYm: firstDayOfThisMonth(),
	workplaceCd: TQC2090_R01_LINE_OPTIONS[0].workplaceCd,
};

/** 상단 InfoBar(검사자·승인자) 값. 저장 시 수정된 행에 적용한다. */
export interface TQC2090_R01Info {
	inspectorEmp: string;
	inspectorEmpNm: string;
	approvorEmpno: string;
	approvorEmpNm: string;
}

export const defaultTQC2090_R01Info: TQC2090_R01Info = {
	inspectorEmp: '',
	inspectorEmpNm: '',
	approvorEmpno: '',
	approvorEmpNm: '',
};

export interface Store_TQC2090_R01 {
	// 검색
	TQC2090_R01Filter: TQC2090_R01Filter;
	TQC2090_R01PastFilter: TQC2090_R01Filter;
	setTQC2090_R01Filter: (filter: Partial<TQC2090_R01Filter>) => void;
	setTQC2090_R01PastFilter: (filter: Partial<TQC2090_R01Filter>) => void;

	// InfoBar
	TQC2090_R01Info: TQC2090_R01Info;
	setTQC2090_R01Info: (info: Partial<TQC2090_R01Info>) => void;

	// 목록
	TQC2090_R01List: TQC2090_R01Dto[];
	setTQC2090_R01List: (list: TQC2090_R01Dto[]) => void;
	updateTQC2090_R01List: (valueIndex: number, data: TQC2090_R01Dto) => void;

	// 원본 (변경 감지)
	originalTQC2090_R01List: TQC2090_R01Dto[];
	setOriginalTQC2090_R01List: (list: TQC2090_R01Dto[]) => void;

	// 선택 (행 하이라이트)
	selectedTQC2090_R01List: string[];
	setSelectedTQC2090_R01List: (idList: string[]) => void;
	addSelectedTQC2090_R01List: (idList: string[]) => void;
	removeSelectedTQC2090_R01List: (idList?: string[]) => void;
	initSelectedTQC2090_R01List: () => void;
	allSelectedTQC2090_R01List: () => void;

	// 컬럼 상태
	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;

	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;

	allCount: number;
	setAllCount: (allCount: number) => void;

	initData: () => void;
}

/**
 * 서버 목록에 그리드 식별/정렬용 인덱스를 1부터 부여한다.
 * 기본값 DTO 를 먼저 펼쳐 누락 필드를 채운 뒤 서버 값으로 덮어쓴다. (TQC1050_R01 패턴)
 */
const withIndex = (list: TQC2090_R01Dto[]): TQC2090_R01Dto[] => {
	let idx = 1;
	return list.map((item) => {
		const row = {
			...defaultTQC2090_R01Dto,
			...item,
			valueIndex: item.valueIndex > 0 ? item.valueIndex : idx,
			orderIndex: item.orderIndex > -1 ? item.orderIndex : idx,
			orderIndexSeq: Math.max(item.orderIndexSeq ?? 0, 0),
		};
		idx++;
		return row;
	});
};

export const useTQC2090_R01Store = create<Store_TQC2090_R01>((set) => ({
	TQC2090_R01Filter: defaultTQC2090_R01Filter,
	TQC2090_R01PastFilter: defaultTQC2090_R01Filter,
	setTQC2090_R01Filter: (filter) =>
		set((state) => ({ TQC2090_R01Filter: { ...state.TQC2090_R01Filter, ...filter } })),
	setTQC2090_R01PastFilter: (filter) =>
		set((state) => ({ TQC2090_R01PastFilter: { ...state.TQC2090_R01PastFilter, ...filter } })),

	TQC2090_R01Info: defaultTQC2090_R01Info,
	setTQC2090_R01Info: (info) =>
		set((state) => ({ TQC2090_R01Info: { ...state.TQC2090_R01Info, ...info } })),

	TQC2090_R01List: [],
	setTQC2090_R01List: (list) => set({ TQC2090_R01List: withIndex(list) }),
	updateTQC2090_R01List: (valueIndex, data) =>
		set((state) => ({
			TQC2090_R01List: state.TQC2090_R01List.map((item) =>
				item.valueIndex === valueIndex ? data : item,
			),
		})),

	originalTQC2090_R01List: [],
	setOriginalTQC2090_R01List: (list) => set({ originalTQC2090_R01List: withIndex(list) }),

	selectedTQC2090_R01List: [],
	setSelectedTQC2090_R01List: (idList) => set({ selectedTQC2090_R01List: idList }),
	addSelectedTQC2090_R01List: (idList) =>
		set((state) => ({
			selectedTQC2090_R01List: [...state.selectedTQC2090_R01List, ...idList],
		})),
	removeSelectedTQC2090_R01List: (idList?) =>
		set((state) => ({
			selectedTQC2090_R01List: state.selectedTQC2090_R01List.filter((v) =>
				idList ? !idList.includes(v) : false,
			),
		})),
	initSelectedTQC2090_R01List: () => set({ selectedTQC2090_R01List: [] }),
	allSelectedTQC2090_R01List: () =>
		set((state) => ({
			selectedTQC2090_R01List: state.TQC2090_R01List.map((item) => item.valueIndex.toString()),
		})),

	columnState: null,
	setColumnState: (state) => set({ columnState: state }),

	scrollTop: null,
	setScrollTop: (top) => set({ scrollTop: top }),

	allCount: 0,
	setAllCount: (allCount) => set({ allCount }),

	initData: () =>
		set({
			TQC2090_R01Filter: defaultTQC2090_R01Filter,
			TQC2090_R01PastFilter: defaultTQC2090_R01Filter,
			TQC2090_R01Info: defaultTQC2090_R01Info,
			TQC2090_R01List: [],
			originalTQC2090_R01List: [],
			selectedTQC2090_R01List: [],
			scrollTop: null,
			allCount: 0,
		}),
}));

